import { Stage, TerminalOutcome } from "./audio-pipeline.types";

/**
 * Canonical stage order. The pipeline walks this sequence, skipping
 * `UploadSource` and `RetranscribeFromSource` when the transcript resolves to
 * `local`. `Done` is the only terminal stage.
 */
export const STAGE_ORDER: readonly Stage[] = [
  Stage.Recording,
  Stage.Finalize,
  Stage.ValidateAudio,
  Stage.ResolveTranscriptSource,
  Stage.UploadSource,
  Stage.RetranscribeFromSource,
  Stage.Transform,
  Stage.Done,
];

/**
 * Allowed forward transitions. Any transition not listed here is a bug and
 * will throw in the pipeline store. `Done` can be reached from any working
 * stage as a failure sink — the outcome disambiguates why.
 */
export const ALLOWED_TRANSITIONS: Readonly<Record<Stage, readonly Stage[]>> = {
  [Stage.Recording]: [Stage.Finalize],
  [Stage.Finalize]: [Stage.ValidateAudio, Stage.Done],
  [Stage.ValidateAudio]: [Stage.ResolveTranscriptSource, Stage.Done],
  [Stage.ResolveTranscriptSource]: [Stage.UploadSource, Stage.Transform, Stage.Done],
  [Stage.UploadSource]: [Stage.RetranscribeFromSource, Stage.Done],
  [Stage.RetranscribeFromSource]: [Stage.Transform, Stage.Done],
  [Stage.Transform]: [Stage.Done],
  [Stage.Done]: [],
};

/**
 * Default terminal outcome when a stage fails unexpectedly. Classification
 * stages (`ValidateAudio`, `ResolveTranscriptSource`) route to their own
 * outcomes via explicit branching in the pipeline store — the values here
 * are only the fallback when the stage worker itself throws.
 */
export const STAGE_FAILURE_OUTCOME: Readonly<Record<Stage, TerminalOutcome>> = {
  [Stage.Recording]: TerminalOutcome.UnexpectedFailed,
  [Stage.Finalize]: TerminalOutcome.UnexpectedFailed,
  [Stage.ValidateAudio]: TerminalOutcome.UnexpectedFailed,
  [Stage.ResolveTranscriptSource]: TerminalOutcome.UnexpectedFailed,
  [Stage.UploadSource]: TerminalOutcome.UploadFailed,
  [Stage.RetranscribeFromSource]: TerminalOutcome.RetranscribeFailed,
  [Stage.Transform]: TerminalOutcome.TransformFailed,
  [Stage.Done]: TerminalOutcome.UnexpectedFailed,
};

export function isTransitionAllowed(from: Stage, to: Stage): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}
